import React, { useState, useEffect } from 'react'
import {
  getManagedTenantsIntuneStatus,
  setManagedTenantAzureId,
  getManagedTenantConsentUrl,
} from '../services/api'
import { errorText } from '../utils/errorText'

interface ManagedTenantIntune {
  tenant_id: string
  name: string
  display_name: string
  azure_tenant_id: string | null
  intune_status: 'connected' | 'not_connected' | 'no_azure_tenant'
  intune_method: 'express' | 'advanced' | null
  connected_at: string | null
}

const MSPIntuneOnboarding: React.FC = () => {
  const [tenants, setTenants] = useState<ManagedTenantIntune[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [batchRunning, setBatchRunning] = useState(false)
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; currentName: string } | null>(null)
  const [editingAzureId, setEditingAzureId] = useState<string | null>(null)
  const [azureIdInput, setAzureIdInput] = useState('')

  useEffect(() => {
    loadStatus()
  }, [])

  const loadStatus = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getManagedTenantsIntuneStatus()
      setTenants(data.tenants)
    } catch (err: any) {
      setError(errorText(err, 'Failed to load managed tenants'))
    } finally {
      setLoading(false)
    }
  }

  const handleSelectAll = () => {
    const connectable = tenants.filter(t => t.intune_status === 'not_connected')
    if (selected.size === connectable.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(connectable.map(t => t.tenant_id)))
    }
  }

  const toggleSelect = (tenantId: string) => {
    const next = new Set(selected)
    if (next.has(tenantId)) next.delete(tenantId)
    else next.add(tenantId)
    setSelected(next)
  }

  const handleSetAzureId = async (tenantId: string) => {
    if (!azureIdInput || azureIdInput.length !== 36) {
      setError('Azure Tenant ID must be a valid GUID (36 characters)')
      return
    }

    try {
      await setManagedTenantAzureId(tenantId, azureIdInput)
      setEditingAzureId(null)
      setAzureIdInput('')
      setSuccessMessage('Azure Tenant ID updated')
      await loadStatus()
    } catch (err: any) {
      setError(errorText(err, 'Failed to set Azure Tenant ID'))
    }
  }

  const handleBatchConsent = async () => {
    const toConnect = tenants.filter(t => selected.has(t.tenant_id) && t.intune_status === 'not_connected')
    if (toConnect.length === 0) return

    setBatchRunning(true)
    setError(null)
    setSuccessMessage(null)

    let completed = 0

    for (const tenant of toConnect) {
      setBatchProgress({ current: completed + 1, total: toConnect.length, currentName: tenant.display_name })

      try {
        const { consent_url } = await getManagedTenantConsentUrl(tenant.tenant_id)

        // Open consent popup and wait for it to close
        await new Promise<void>((resolve) => {
          const popup = window.open(consent_url, 'intune-consent', 'width=600,height=700,scrollbars=yes')
          if (!popup) {
            setError(`Popup blocked for ${tenant.display_name}. Please allow popups and try again.`)
            resolve()
            return
          }
          const timer = setInterval(() => {
            if (popup.closed) {
              clearInterval(timer)
              resolve()
            }
          }, 500)
        })

        completed++
      } catch (err: any) {
        setError(`Failed for ${tenant.display_name}: ${errorText(err)}`)
        break
      }
    }

    setBatchRunning(false)
    setBatchProgress(null)
    setSelected(new Set())

    if (completed > 0) {
      setSuccessMessage(`${completed} of ${toConnect.length} tenants processed. Refreshing status...`)
    }

    // Wait a moment for callbacks to be processed, then reload
    setTimeout(() => loadStatus(), 2000)
  }

  const connectable = tenants.filter(t => t.intune_status === 'not_connected')

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-theme-text-secondary">Loading managed tenants...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-theme-text-primary mb-2">MSP Intune Onboarding</h2>
        <p className="text-theme-text-secondary">
          Connect your managed tenants to Microsoft Intune via GDAP. Requires Application Administrator role in each tenant.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
          <p className="text-green-800 dark:text-green-300">{successMessage}</p>
        </div>
      )}

      {/* Batch Progress */}
      {batchRunning && batchProgress && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            <p className="text-blue-800 dark:text-blue-300">
              Processing {batchProgress.current} of {batchProgress.total}: {batchProgress.currentName}
            </p>
          </div>
          <div className="mt-2 w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Batch Action Bar */}
      {connectable.length > 0 && !batchRunning && (
        <div className="flex items-center justify-between bg-theme-secondary border border-theme-border-primary rounded-lg p-4">
          <div className="text-sm text-theme-text-secondary">
            {selected.size} of {connectable.length} tenants selected for batch onboarding
          </div>
          <button
            onClick={handleBatchConsent}
            disabled={selected.size === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
          >
            Batch Connect to Intune ({selected.size})
          </button>
        </div>
      )}

      {/* Tenant Table */}
      <div className="bg-theme-secondary border border-theme-border-primary rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-theme-border-primary">
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={connectable.length > 0 && selected.size === connectable.length}
                  onChange={handleSelectAll}
                  disabled={connectable.length === 0 || batchRunning}
                  className="rounded"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase">Tenant</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase">Azure AD Tenant ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase">Intune Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase">Method</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant.tenant_id} className="border-b border-theme-border-primary last:border-0">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(tenant.tenant_id)}
                    onChange={() => toggleSelect(tenant.tenant_id)}
                    disabled={tenant.intune_status !== 'not_connected' || batchRunning}
                    className="rounded"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-theme-text-primary">{tenant.display_name}</div>
                  <div className="text-xs text-theme-text-tertiary">{tenant.name}</div>
                </td>
                <td className="px-4 py-3">
                  {editingAzureId === tenant.tenant_id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={azureIdInput}
                        onChange={(e) => setAzureIdInput(e.target.value)}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        className="w-64 px-2 py-1 text-xs border border-theme-border-primary bg-theme-input text-theme-text-primary rounded focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => handleSetAzureId(tenant.tenant_id)}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { setEditingAzureId(null); setAzureIdInput('') }}
                        className="px-2 py-1 text-xs text-theme-text-tertiary hover:text-theme-text-primary"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {tenant.azure_tenant_id ? (
                        <span className="text-xs font-mono text-theme-text-secondary">{tenant.azure_tenant_id}</span>
                      ) : (
                        <span className="text-xs text-yellow-600 dark:text-yellow-400">Not set</span>
                      )}
                      <button
                        onClick={() => { setEditingAzureId(tenant.tenant_id); setAzureIdInput(tenant.azure_tenant_id || '') }}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {tenant.azure_tenant_id ? 'Edit' : 'Set'}
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {tenant.intune_status === 'connected' && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      Connected
                    </span>
                  )}
                  {tenant.intune_status === 'not_connected' && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                      Not Connected
                    </span>
                  )}
                  {tenant.intune_status === 'no_azure_tenant' && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-400">
                      Needs Azure ID
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-theme-text-secondary">
                  {tenant.intune_method === 'express' && 'Express'}
                  {tenant.intune_method === 'advanced' && 'Advanced'}
                  {!tenant.intune_method && '-'}
                </td>
                <td className="px-4 py-3 text-right">
                  {tenant.intune_status === 'not_connected' && (
                    <button
                      onClick={async () => {
                        try {
                          const { consent_url } = await getManagedTenantConsentUrl(tenant.tenant_id)
                          const popup = window.open(consent_url, 'intune-consent', 'width=600,height=700,scrollbars=yes')
                          if (popup) {
                            const timer = setInterval(() => {
                              if (popup.closed) {
                                clearInterval(timer)
                                setTimeout(() => loadStatus(), 1500)
                              }
                            }, 500)
                          }
                        } catch (err: any) {
                          setError(errorText(err, 'Failed to start consent'))
                        }
                      }}
                      disabled={batchRunning}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      Connect
                    </button>
                  )}
                  {tenant.intune_status === 'connected' && tenant.connected_at && (
                    <span className="text-xs text-theme-text-tertiary">
                      {new Date(tenant.connected_at).toLocaleDateString()}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {tenants.length === 0 && (
          <div className="p-8 text-center text-theme-text-tertiary">
            No managed tenants found. Add tenants from the MSP Dashboard first.
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">About GDAP Batch Onboarding</h4>
        <p className="text-sm text-blue-700 dark:text-blue-400">
          With Granular Delegated Admin Privileges (GDAP), you can consent to Intune permissions on behalf of your managed tenants.
          Requires the <strong>Application Administrator</strong> GDAP role in each customer's Azure AD.
          Set each tenant's Azure AD Tenant ID first, then use batch connect.
        </p>
      </div>
    </div>
  )
}

export default MSPIntuneOnboarding
